import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  getApplicationByEnterpriseId,
  getApplicationByUserId
} from "../../actions/application.action";
import Badge from "../../components/badge";
import Modal from "../../components/modal";
import Table, { TableColumns } from "../../components/table";
import {
  RetailODApplicationDetails,
  TermLoanApplicationDetails,
  UserApplicationsDetails,
  UserDetails
} from "../../dto";
import { EnterpriseDetails } from "../../dto/enterprise.dto";
import dispatch from "../../middleware";
import {
  ApplicationStatusInfo,
  ApplicationType,
  RoutePaths
} from "../../models";
import { ReduxState } from "../../reducers";
import {
  dateToDDMMYYYY,
  isLoadingActive,
  splitKeyWithSpace
} from "../../utils";
import CreateOrEditApplicationForm from "./createApplicationForm";
import { isUserAnEnterprise } from "./helpers";

interface UserApplicationsProps {
  userSelfieUrl: string | null;
  userDetails: UserDetails | EnterpriseDetails;
}

export default function UserApplications({
  userSelfieUrl,
  userDetails,
}: UserApplicationsProps) {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.application.loadingQueue,
  );
  const applications: UserApplicationsDetails = useSelector(
    (state: ReduxState) => state.application.userApplications,
  );
  const [isModalActive, setIsModalActive] = useState(false);

  const isEnterprise = isUserAnEnterprise(userDetails._id);

  useEffect(() => {
    const entityId = userDetails._id;
    dispatch(
      storeDispatch,
      isEnterprise
        ? getApplicationByEnterpriseId(entityId)
        : getApplicationByUserId(entityId),
    );
  }, []);

  const retailODColumns: TableColumns[] = [
    { field: "_id", label: "Application ID" },
    { field: "createdAt", label: "Date of Application" },
    { field: "maxAmount", label: "Sanctioned Amount" },
    { field: "status", label: "Status" },
    // { field: "applicationType", label: "Application Type" },
    // { field: "maxValidMonths", label: "Max Valid (months)" },
  ];

  const termLoanColumns: TableColumns[] = [
    { field: "_id", label: "Application ID" },
    { field: "createdAt", label: "Date of Application" },
    { field: "approvedAmount", label: "Sanctioned Amount" },
    { field: "tenureInMonths", label: "Tenure (months)" },
    { field: "status", label: "Status" },
    // { field: "applicationType", label: "Application Type" },
  ];

  function getIdLink(id: string) {
    return (
      <Link to={`${RoutePaths.APPLICATIONS}/${id}`} className="table-link">
        {id}
      </Link>
    );
  }
  function getApplicationType(type: ApplicationType) {
    if (type === "RETAIL_OD_APP") return "Earned Wage Access";
    if (type === "TERM_LOAN_APP") return "Term Loan";
    return "";
  }
  function getStatusBadge(status: ApplicationStatusInfo) {
    if (status?.data === "APPROVED")
      return <Badge type="success">Approved</Badge>;
    if (status?.data === "REJECTED")
      return <Badge type="reject">Rejected</Badge>;
    return (
      <Badge type="pending" capitalize>
        {splitKeyWithSpace(status?.data).toLowerCase()}
      </Badge>
    );
  }

  function getTableDataForTerm(applications: TermLoanApplicationDetails[]) {
    return applications.map(
      ({
        _id,
        approvedAmount,
        applicationType,
        tenureInMonths,
        status,
        createdAt,
      }) => ({
        _id: getIdLink(_id),
        applicationType: getApplicationType(applicationType),
        approvedAmount,
        tenureInMonths,
        createdAt: dateToDDMMYYYY(createdAt),
        status: getStatusBadge(status),
      }),
    );
  }

  function getTableDataForOD(applications: RetailODApplicationDetails[]) {
    return applications.map(
      ({
        _id,
        maxAmount,
        maxValidMonths,
        applicationType,
        status,
        createdAt,
      }) => ({
        _id: getIdLink(_id),
        applicationType: getApplicationType(applicationType),
        maxAmount,
        // INFO: Currently in backend we don't have tenor so that adding it in frontend
        tenor: 12,
        maxValidMonths,
        createdAt: dateToDDMMYYYY(createdAt),
        status: getStatusBadge(status),
      }),
    );
  }

  return (
    <>
      <div className="d-flex flex-md-row flex-column justify-content-between align-items-start mb-4">
        <h3 className="user-page-title mb-2 mb-md-0">
          Earned Wage Access Applications
        </h3>
      </div>
      <Table
        loading={isLoadingActive(loadingQueue)}
        columns={retailODColumns}
        data={getTableDataForOD(applications.retailODApplications)}
      />
      <div className="mb-4">
        <h3 className="user-page-title">Term Loan Applications</h3>
      </div>
      <Table
        loading={isLoadingActive(loadingQueue)}
        columns={termLoanColumns}
        data={getTableDataForTerm(applications.termLoanApplications)}
      />
      <Modal
        open={isModalActive}
        title="New application"
        size="large"
        hasSubmitButton={false}
        hasCancelButton={false}
        onClose={() => setIsModalActive(false)}
      >
        <CreateOrEditApplicationForm
          selfieUrl={userSelfieUrl}
          userDetails={userDetails}
        />
      </Modal>
    </>
  );
}
